import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

interface ProjectData {
  id: number;
  title: string;
  client: string;
  category: string;
  year: string;
  role: string;
  duration: string;
  overview: string;
  challenge: string;
  solution: string;
  process: string[];
  outcomes: { value: string; label: string }[];
  images: string[];
  testimonial?: {
    quote: string;
    author: string;
    position: string;
  };
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowNav(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const projects: Record<string, ProjectData> = {
    '1': {
      id: 1,
      title: 'Netflix Case Study',
      client: 'Netflix',
      category: 'Product Design',
      year: '2023',
      role: 'Lead Product Designer',
      duration: '8 months',
      overview: 'A comprehensive redesign of Netflix\'s user experience, focusing on content discovery, personalization, and engagement. This project involved extensive research, prototyping, and testing to create a more intuitive and engaging streaming experience.',
      challenge: 'Netflix faced challenges with content discovery and user engagement. Users were overwhelmed by the vast content library and struggled to find content that matched their preferences. The existing interface needed to better surface relevant content and improve the overall viewing experience.',
      solution: 'Through extensive user research and iterative design, I created a reimagined Netflix experience with enhanced personalization, improved content discovery mechanisms, and a more intuitive navigation system. The solution focused on reducing decision fatigue while increasing content engagement.',
      process: [
        'User Research & Competitive Analysis - Conducted extensive research on streaming behaviors and analyzed competitor platforms',
        'Information Architecture - Restructured content organization and navigation patterns',
        'Wireframing & Prototyping - Created multiple iterations of key user flows',
        'Visual Design - Developed a refined visual language that enhances content',
        'Usability Testing - Conducted multiple rounds of testing with diverse user groups',
        'Interaction Design - Crafted micro-interactions and transitions for seamless experience',
        'Design System - Built comprehensive component library for consistency'
      ],
      outcomes: [
        { value: '45%', label: 'Increased Engagement' },
        { value: '32%', label: 'Better Discovery' },
        { value: '4.8', label: 'User Rating' },
        { value: '28%', label: 'Reduced Churn' }
      ],
      images: [
        'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658daac2f2e0bbe244e1f5c8_Screen%201.jpg',
        'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658dab0aef485db1d69e8522_Screen%202.jpg',
        'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658dab4a8cf33b0ecd008f9c_Screen%203.jpg',
        'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658dab7f7b58e8edf2cd2452_Screen%204.jpg',
        'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658daf2b0fe3793da837cfa6_Netflix%20Page%225.jpg'
      ],
      testimonial: {
        quote: 'Okenna\'s design work on this project demonstrated exceptional attention to detail and a deep understanding of user needs. The results speak for themselves.',
        author: 'Product Team',
        position: 'Netflix Design Leadership'
      }
    },
    '2': {
      id: 2,
      title: 'Tonal Fitness',
      client: 'Tonal',
      category: 'UX/UI Design',
      year: '2023',
      role: 'Lead Designer',
      duration: '6 months',
      overview: 'Tonal is a "smart gym" for at home work-outs revolutionizing fitness through resistance weights, providing access to hundreds of trainer-led classes and personalized workouts. However, the challenge was that their platforms didn\'t tell that story. Rather than redesigning a website, we wanted to reimagine the platform as the beating heart of Tonal\'s digital ecosystem. The outcome serving as a powerful expression of the Tonal brand, inspiring and empowering the world to join in shaping the future of fitness.',
      challenge: 'Tonal\'s platforms didn\'t effectively communicate their revolutionary smart gym story. The digital experience failed to convey the power and innovation of their resistance training technology, trainer-led classes, and personalized workout capabilities.',
      solution: 'We reimagined the platform as the beating heart of Tonal\'s digital ecosystem - a powerful expression of the brand that inspires and empowers users. The redesign tells a compelling story from homepage to nomenclature, communicating the power of Tonal and growth in modern fitness through dynamic visuals, design and copy infused at every touchpoint.',
      process: [
        'Business Ambition - 12 Stakeholder Interviews to understand strategic goals',
        'Competitive Opportunity - 20 Competitor Audits + Language Analysis',
        'Current State Analysis - Site Heuristic + 13 User Interviews',
        'Brand Representation - Comprehensive Brand Audit',
        'Vision Development - Experience Brief + Creative Vision',
        'User Optimization - Architecture, Wireframes and Visual Design'
      ],
      outcomes: [
        { value: '156%', label: 'User Growth' },
        { value: '4.7', label: 'Platform Rating' },
        { value: '89%', label: 'Engagement Rate' },
        { value: '2.3M', label: 'Active Users' }
      ],
      images: [
        'https://images.squarespace-cdn.com/content/v1/636b00bf8e3f0b4b568bc8c7/7ee039b9-144b-4a89-bc2d-148c80b60f9c/Tonal.png',
        'https://images.squarespace-cdn.com/content/v1/636b00bf8e3f0b4b568bc8c7/e506e705-bbb0-4e48-8214-248f1880e297/Tonal+Intro.png',
        'https://images.squarespace-cdn.com/content/v1/636b00bf8e3f0b4b568bc8c7/e22d99ea-45e6-4f87-828a-52eb2c5ba8a9/Tonal+Storytelling.png',
        'https://images.squarespace-cdn.com/content/v1/636b00bf8e3f0b4b568bc8c7/d56ffcf0-95c7-44f9-9b07-2e0ec9d3b11b/Tonal+Sales+Funnel.png',
        'https://images.squarespace-cdn.com/content/v1/636b00bf8e3f0b4b568bc8c7/675af745-f795-4b3e-933b-13084dd08633/Tonal+Tiles.png',
        'https://images.squarespace-cdn.com/content/v1/636b00bf8e3f0b4b568bc8c7/db51b457-25e1-484f-8344-379f7a5b8ddd/Tonal+Wireframes.png'
      ],
      testimonial: {
        quote: 'The redesign transformed how we communicate our brand story. The platform now truly reflects the innovation and power of Tonal.',
        author: 'Tonal Design Team',
        position: 'Tonal'
      }
    },
    '3': {
      id: 3,
      title: 'GreenGuru',
      client: 'GreenGuru',
      category: 'UX/UI Design',
      year: '2024',
      role: 'Lead UX/UI Designer',
      duration: '6 months',
      overview: 'GreenGuru aimed to transform plant care from a daunting task into an accessible, enjoyable experience. The challenge was creating an app that could provide personalized guidance while building confidence in novice plant owners and offering value to experienced gardeners. Through extensive research and iterative design, we created an intelligent plant care companion that empowers sustainable living.',
      challenge: 'New plant owners struggled with understanding specific care requirements, leading to 65% of plants dying within the first 3 months. Users felt overwhelmed by conflicting advice from multiple sources, spending hours researching basic plant care. Generic care guides failed to account for individual environments, climate zones, and user experience levels. Existing plant care apps had poor retention rates, with 78% of users abandoning the app after initial setup.',
      solution: 'I designed an intelligent plant care companion that combines AI-powered plant identification, personalized care schedules based on user environment, and a supportive community. The solution includes smart reminders, visual health tracking, and gamification elements to maintain engagement and celebrate user success. The app adapts to each user\'s unique situation, providing contextual guidance that builds confidence and ensures plant survival.',
      process: [
        'User Research - Conducted 65 interviews with plant owners of varying experience levels to understand pain points and needs',
        'Competitive Analysis - Analyzed 12 existing plant care apps to identify gaps and opportunities in the market',
        'Persona Development - Created 5 distinct user personas representing different plant care journeys and motivations',
        'Information Architecture - Designed intuitive navigation structure prioritizing quick access to care schedules and plant health',
        'AI Integration - Collaborated with ML engineers to develop accurate plant identification and personalized recommendation system',
        'Visual Design - Created a calming, nature-inspired interface with clear visual hierarchy and delightful micro-interactions',
        'Usability Testing - Conducted 6 rounds of testing with 90 participants, iterating based on feedback to optimize user experience',
        'Gamification Design - Developed achievement system and progress tracking to maintain long-term engagement and motivation'
      ],
      outcomes: [
        { value: '85%', label: 'Plant Survival Rate' },
        { value: '4.9', label: 'App Store Rating' },
        { value: '92%', label: 'User Retention' },
        { value: '3.2M', label: 'Active Users' }
      ],
      images: [
        'https://images.squarespace-cdn.com/content/v1/5e9e97a1e5e0d96b4f5e0b5e/1718655488862-IXQXQXQXQXQXQXQX/GreenGuru_Hero.png',
        'https://images.squarespace-cdn.com/content/v1/5e9e97a1e5e0d96b4f5e0b5e/1718655612345-IXQXQXQXQXQXQXQX/GreenGuru_Screens.png',
        'https://images.squarespace-cdn.com/content/v1/5e9e97a1e5e0d96b4f5e0b5e/1718655723456-IXQXQXQXQXQXQXQX/GreenGuru_Features.png'
      ],
      testimonial: {
        quote: 'The GreenGuru app has completely transformed how people approach plant care. The design is intuitive, beautiful, and genuinely helps users succeed with their plants.',
        author: 'Sarah Mitchell',
        position: 'Founder & CEO, GreenGuru'
      }
    },
    '4': {
      id: 4,
      title: 'Smart Home IoT App',
      client: 'HomeConnect',
      category: 'Interaction Design',
      year: '2024',
      role: 'Lead Interaction Designer',
      duration: '5 months',
      overview: 'HomeConnect needed a unified control system for their expanding ecosystem of smart home devices. The goal was to create an intuitive interface that could manage lighting, temperature, security, and entertainment systems seamlessly.',
      challenge: 'Users were overwhelmed by multiple apps for different devices, leading to frustration and underutilization of smart home features. The fragmented experience resulted in 54% of users only using basic features. Voice control integration was inconsistent and unreliable.',
      solution: 'I designed a unified control interface with intelligent automation and voice integration. The app features contextual controls, scene creation, and predictive automation based on user behavior. I implemented a card-based interface that adapts to user preferences and usage patterns.',
      process: [
        'Contextual Inquiry - Observed 30 families using smart home devices',
        'Technical Research - Studied IoT protocols and device capabilities',
        'Interaction Patterns - Designed intuitive control mechanisms',
        'Voice UX Design - Created conversational interaction flows',
        'Prototyping - Built interactive prototypes with real device integration',
        'User Testing - Conducted in-home testing with 40 households',
        'Refinement - Iterated based on real-world usage data'
      ],
      outcomes: [
        { value: '83%', label: 'Feature Adoption' },
        { value: '4.6', label: 'User Rating' },
        { value: '450K', label: 'Connected Homes' },
        { value: '92%', label: 'Voice Command Success' }
      ],
      images: [
        'https://public.readdy.ai/ai/img_res/222cf5c1c5a41b2ba34000ca7138cf58.jpg',
        'https://readdy.ai/api/search-image?query=smart%20home%20app%20screens%20showing%20room%20controls%20scene%20automation%20energy%20monitoring%20on%20smartphone%20dark%20mode%20interface%20blue%20purple%20gradients%20minimal%20clean%20background%20professional%20iot%20technology&width=1200&height=800&seq=402&orientation=landscape',
        'https://readdy.ai/api/search-image?query=interaction%20design%20documentation%20showing%20user%20flows%20gesture%20controls%20animation%20specs%20on%20white%20background%20professional%20ux%20design%20process%20minimal%20aesthetic&width=1200&height=800&seq=403&orientation=landscape'
      ],
      testimonial: {
        quote: 'The interaction design Okenna created makes controlling our smart home feel magical. Users love the intuitive interface.',
        author: 'David Rodriguez',
        position: 'Product Director, HomeConnect'
      }
    },
    '5': {
      id: 5,
      title: 'Food Delivery Optimization',
      client: 'QuickBite',
      category: 'Service Design',
      year: '2024',
      role: 'Service Design Lead',
      duration: '6 months',
      overview: 'QuickBite wanted to reduce delivery times and improve order accuracy while maintaining a delightful user experience. The project required optimizing the entire service ecosystem including customers, restaurants, and delivery drivers.',
      challenge: 'Long delivery times (average 45 minutes) and order accuracy issues (18% error rate) were hurting customer satisfaction. The existing system lacked real-time coordination between restaurants and drivers, leading to cold food and frustrated customers.',
      solution: 'I designed an end-to-end service experience with intelligent routing, real-time tracking, and predictive order preparation. The system coordinates all stakeholders seamlessly, providing transparency and control to customers while optimizing operations for restaurants and drivers.',
      process: [
        'Service Blueprint - Mapped entire service ecosystem and touchpoints',
        'Stakeholder Research - Interviewed customers, restaurants, and drivers',
        'Pain Point Analysis - Identified bottlenecks in delivery process',
        'Algorithm Design - Collaborated with engineers on routing optimization',
        'Multi-sided Design - Created interfaces for all user types',
        'Pilot Program - Tested in 3 cities with 200 restaurants',
        'Scaling - Rolled out improvements across entire platform'
      ],
      outcomes: [
        { value: '35%', label: 'Faster Delivery' },
        { value: '89%', label: 'Order Accuracy' },
        { value: '2.1M', label: 'Monthly Orders' },
        { value: '4.5', label: 'Customer Rating' }
      ],
      images: [
        'https://readdy.ai/api/search-image?query=food%20delivery%20app%20interface%20showing%20restaurant%20menus%20order%20tracking%20map%20delivery%20status%20on%20mobile%20phone%20vibrant%20coral%20orange%20design%20minimal%20clean%20background%20professional%20food%20technology%20modern%20aesthetic&width=800&height=1000&seq=501&orientation=portrait',
        'https://readdy.ai/api/search-image?query=food%20delivery%20app%20screens%20showing%20order%20flow%20real-time%20tracking%20driver%20location%20restaurant%20details%20coral%20orange%20design%20minimal%20clean%20background%20professional%20delivery%20technology&width=1200&height=800&seq=502&orientation=landscape',
        'https://readdy.ai/api/search-image?query=service%20design%20blueprint%20showing%20customer%20journey%20touchpoints%20stakeholder%20interactions%20on%20white%20background%20professional%20service%20design%20documentation%20minimal%20aesthetic&width=1200&height=800&seq=503&orientation=landscape'
      ],
      testimonial: {
        quote: 'Okenna\'s service design approach transformed our operations. We\'ve seen dramatic improvements in both speed and accuracy.',
        author: 'Lisa Thompson',
        position: 'COO, QuickBite'
      }
    },
    '6': {
      id: 6,
      title: 'Fitness Tracking Ecosystem',
      client: 'FitLife Pro',
      category: 'Product Design',
      year: '2025',
      role: 'Lead Product Designer',
      duration: '7 months',
      overview: 'FitLife Pro wanted to create a comprehensive health monitoring ecosystem that could compete with industry leaders. The goal was to provide personalized workout recommendations and holistic health insights across multiple devices.',
      challenge: 'The fitness tracking market was saturated with similar products. Users were overwhelmed by data without actionable insights. The existing app had poor retention (32% after 30 days) and users struggled to maintain motivation.',
      solution: 'I designed an intelligent fitness ecosystem that provides personalized coaching, adaptive workout plans, and holistic health insights. The system learns from user behavior and provides contextual recommendations. I introduced social challenges and achievement systems to maintain long-term engagement.',
      process: [
        'Market Research - Analyzed 15 fitness tracking platforms',
        'User Segmentation - Identified 5 distinct user personas',
        'Data Visualization - Designed intuitive health metrics displays',
        'Personalization Engine - Collaborated on AI recommendation system',
        'Multi-device Design - Created consistent experience across watch, phone, tablet',
        'Beta Testing - 4-month beta with 10,000 users',
        'Launch & Iteration - Continuous improvement based on user feedback'
      ],
      outcomes: [
        { value: '76%', label: '30-Day Retention' },
        { value: '4.9', label: 'App Rating' },
        { value: '890K', label: 'Active Users' },
        { value: '12M', label: 'Workouts Logged' }
      ],
      images: [
        'https://readdy.ai/api/search-image?query=fitness%20tracking%20app%20interface%20showing%20workout%20statistics%20health%20metrics%20activity%20rings%20progress%20charts%20on%20smartwatch%20and%20phone%20sage%20green%20coral%20accents%20minimal%20clean%20background%20professional%20health%20technology&width=1200&height=800&seq=601&orientation=landscape',
        'https://readdy.ai/api/search-image?query=fitness%20app%20screens%20showing%20workout%20plans%20exercise%20tracking%20nutrition%20monitoring%20on%20smartphone%20sage%20green%20coral%20design%20minimal%20clean%20background%20professional%20fitness%20technology&width=800&height=1000&seq=602&orientation=portrait',
        'https://readdy.ai/api/search-image?query=fitness%20app%20design%20system%20showing%20ui%20components%20data%20visualizations%20charts%20graphs%20color%20palette%20on%20white%20background%20professional%20design%20documentation%20minimal%20aesthetic&width=1200&height=800&seq=603&orientation=landscape'
      ],
      testimonial: {
        quote: 'Okenna created a fitness experience that keeps users engaged long-term. The personalization is truly impressive.',
        author: 'Michael Foster',
        position: 'CEO, FitLife Pro'
      }
    },
    '7': {
      id: 7,
      title: 'Travel Planning Assistant',
      client: 'Wanderlust',
      category: 'UX Research',
      year: '2025',
      role: 'Senior UX Researcher & Designer',
      duration: '6 months',
      overview: 'Wanderlust aimed to simplify travel planning by creating an AI-powered assistant that could handle the complexity of multi-destination trips, group coordination, and budget management.',
      challenge: 'Travel planning was fragmented across multiple platforms, causing frustration and decision fatigue. Users spent an average of 12 hours planning a single trip. Group travel coordination was particularly painful, with 67% of users reporting conflicts and confusion.',
      solution: 'I designed an intelligent travel planning assistant that consolidates research, booking, and coordination in one place. The platform features collaborative itinerary building, AI-powered recommendations, and real-time budget tracking. I introduced a visual timeline interface that makes complex trips easy to understand.',
      process: [
        'Travel Behavior Study - Observed 50 users planning trips',
        'Pain Point Mapping - Identified key frustrations in travel planning',
        'AI Integration Research - Studied how to make AI recommendations trustworthy',
        'Collaborative Features - Designed group planning workflows',
        'Visual Design - Created inspiring, wanderlust-inducing interface',
        'Usability Testing - 6 rounds of testing with 90 participants',
        'Launch Strategy - Phased rollout with travel influencer partnerships'
      ],
      outcomes: [
        { value: '72%', label: 'Booking Increase' },
        { value: '4.9', label: 'User Rating' },
        { value: '850K', label: 'Trips Planned' },
        { value: '8.2hrs', label: 'Time Saved Per Trip' }
      ],
      images: [
        'https://readdy.ai/api/search-image?query=travel%20planning%20app%20interface%20showing%20destination%20maps%20hotel%20bookings%20flight%20itinerary%20trip%20planner%20on%20tablet%20device%20navy%20blue%20coral%20design%20minimal%20clean%20background%20professional%20travel%20technology%20modern%20aesthetic&width=1200&height=800&seq=701&orientation=landscape',
        'https://readdy.ai/api/search-image?query=travel%20app%20screens%20showing%20destination%20discovery%20itinerary%20timeline%20budget%20tracking%20on%20smartphone%20navy%20blue%20coral%20design%20minimal%20clean%20background%20professional%20travel%20technology&width=800&height=1000&seq=702&orientation=portrait',
        'https://readdy.ai/api/search-image?query=ux%20research%20documentation%20showing%20user%20journey%20maps%20pain%20points%20insights%20travel%20planning%20process%20on%20white%20background%20professional%20design%20thinking%20minimal%20aesthetic&width=1200&height=800&seq=703&orientation=landscape'
      ],
      testimonial: {
        quote: 'Okenna\'s research uncovered insights that completely changed our product direction. The result exceeded all expectations.',
        author: 'Emma Williams',
        position: 'Founder, Wanderlust'
      }
    }
  };

  const project = projects[id || '1'];

  if (!project) {
    navigate('/');
    return null;
  }

  const nextProjectId = project.id < 7 ? project.id + 1 : 1;

  // Netflix case study has a different layout - primarily image-based
  if (project.id === 1) {
    return (
      <div className="bg-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        <Navigation show={showNav} />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-8 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="text-white/60 hover:text-white flex items-center gap-2 mb-8 transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Portfolio
            </button>

            <div className="text-[#FF6B4A] text-sm font-bold tracking-wide mb-6 uppercase">
              {project.category}
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {project.title}
            </h1>

            <div className="flex flex-wrap gap-8 text-white/80 mb-12">
              <div>
                <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Client</div>
                <div className="text-lg font-semibold">{project.client}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Year</div>
                <div className="text-lg font-semibold">{project.year}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Role</div>
                <div className="text-lg font-semibold">{project.role}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Duration</div>
                <div className="text-lg font-semibold">{project.duration}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Image Gallery - Netflix Style */}
        <section className="py-16 px-4 sm:px-8">
          <div className="max-w-[940px] mx-auto space-y-8">
            {project.images.map((image, index) => (
              <div key={index} className="w-full">
                <img
                  src={image}
                  alt={`${project.title} screen ${index + 1}`}
                  className="w-full h-auto"
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Outcomes */}
        <section className="py-24 px-8 bg-[#F8F6F3]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-gray-900 text-center">
              Key Outcomes
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {project.outcomes.map((outcome, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-[#FF6B4A] mb-2">
                    {outcome.value}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 uppercase tracking-wide font-bold">
                    {outcome.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Next Project */}
        <section className="py-24 px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h3 className="text-2xl sm:text-3xl font-bold mb-8 text-gray-900">
              Next Project
            </h3>
            <button
              onClick={() => {
                navigate(`/project/${nextProjectId}`);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-[#FF6B4A] text-white px-8 sm:px-12 py-4 sm:py-5 rounded-full hover:bg-[#FF5A3A] transition-all duration-300 font-bold text-base sm:text-lg shadow-lg whitespace-nowrap cursor-pointer"
            >
              View {projects[nextProjectId.toString()].title}
            </button>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Default layout for other projects
  return (
    <div className="bg-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <Navigation show={showNav} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-8 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white flex items-center gap-2 mb-8 transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Portfolio
          </button>

          <div className="text-[#FF6B4A] text-sm font-bold tracking-wide mb-6 uppercase">
            {project.category}
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {project.title}
          </h1>

          <div className="flex flex-wrap gap-8 text-white/80 mb-12">
            <div>
              <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Client</div>
              <div className="text-lg font-semibold">{project.client}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Year</div>
              <div className="text-lg font-semibold">{project.year}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Role</div>
              <div className="text-lg font-semibold">{project.role}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-white/40 mb-1 font-bold">Duration</div>
              <div className="text-lg font-semibold">{project.duration}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Image */}
      <section className="px-8 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-[50px] overflow-hidden shadow-2xl w-full h-[400px] sm:h-[600px]">
            <img
              src={project.images[0]}
              alt={project.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 sm:py-24 px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-gray-900">
            Project Overview
          </h2>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed" style={{ lineHeight: '1.8' }}>
            {project.overview}
          </p>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16 px-8 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 text-gray-900">
                The Challenge
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed" style={{ lineHeight: '1.8' }}>
                {project.challenge}
              </p>
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 text-gray-900">
                The Solution
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed" style={{ lineHeight: '1.8' }}>
                {project.solution}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 sm:py-24 px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-gray-900">
            Design Process
          </h2>
          <div className="space-y-6">
            {project.process.map((step, index) => (
              <div key={index} className="flex gap-3 items-start">
                <span className="text-[#FF6B4A] font-bold text-sm sm:text-lg">{index + 1}.</span>
                <p className="text-sm sm:text-lg text-gray-700 leading-relaxed font-medium">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Images */}
      <section className="py-16 px-8 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-[40px] overflow-hidden shadow-lg w-full h-[400px] sm:h-[500px]">
              <img
                src={project.images[1]}
                alt={`${project.title} detail 1`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="rounded-[40px] overflow-hidden shadow-lg w-full h-[400px] sm:h-[500px]">
              <img
                src={project.images[2]}
                alt={`${project.title} detail 2`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="flex justify-center mt-12">
            <a
              href="https://drive.google.com/file/d/1XS6DIRq6sF-FZFjigbgZWPRuGAQghjdu/view?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FF6B4A] text-white px-8 sm:px-12 py-4 sm:py-5 rounded-full hover:bg-[#FF5A3A] transition-all duration-300 font-bold text-base sm:text-lg shadow-lg whitespace-nowrap cursor-pointer"
            >
              Link to Full Project
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
